import React, { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  padding: 20px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 420px;
  backdrop-filter: blur(10px);
`;

const Icon = styled.div`
  font-size: 48px;
  text-align: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  color: white;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  text-align: center;
  margin: 0 0 32px 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: white;
  font-size: 15px;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
    background: rgba(255, 255, 255, 0.08);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const PasswordRequirements = styled.div`
  padding: 12px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  
  ul {
    margin: 8px 0 0 16px;
    padding: 0;
    
    li {
      margin-bottom: 4px;
    }
  }
`;

const SubmitButton = styled.button<{ disabled: boolean }>`
  padding: 16px;
  background: ${props => props.disabled 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'linear-gradient(135deg, #6366f1, #8b5cf6)'};
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  margin-top: 8px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #ef4444;
  font-size: 13px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  padding: 12px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 8px;
  color: #10b981;
  font-size: 13px;
  text-align: center;
`;

interface ForcePasswordChangeProps {
  onPasswordChanged: () => void;
  currentPassword?: string; // Optional - if user just logged in with temp password
}

const ForcePasswordChange: React.FC<ForcePasswordChangeProps> = ({ 
  onPasswordChanged,
  currentPassword: initialCurrentPassword 
}) => {
  const [currentPassword, setCurrentPassword] = useState(initialCurrentPassword || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isValid = () => {
    return (
      currentPassword.length > 0 &&
      newPassword.length >= 12 &&
      newPassword === confirmPassword &&
      newPassword !== currentPassword
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValid()) return;
    
    setLoading(true);
    setError(null);

    try {
      await axios.post(`${API_BASE_URL}/api/auth/change-password`, {
        currentPassword,
        newPassword
      });
      
      setSuccess(true);
      setTimeout(() => {
        onPasswordChanged();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <Icon>🔐</Icon>
        <Title>Change Your Password</Title>
        <Subtitle>
          Your account requires a password change before you can continue.
        </Subtitle>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>Password changed successfully! Redirecting...</SuccessMessage>}

          <InputGroup>
            <Label>Current Password</Label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter your current/temporary password"
              disabled={loading || success}
            />
          </InputGroup>

          <InputGroup>
            <Label>New Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter your new password"
              disabled={loading || success}
            />
          </InputGroup>

          <InputGroup>
            <Label>Confirm New Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              disabled={loading || success}
            />
          </InputGroup>

          <PasswordRequirements>
            <strong>Password requirements:</strong>
            <ul>
              <li style={{ color: newPassword.length >= 12 ? '#10b981' : 'inherit' }}>
                At least 12 characters, with an uppercase letter, a digit and a special character
              </li>
              <li style={{ color: newPassword === confirmPassword && newPassword.length > 0 ? '#10b981' : 'inherit' }}>
                Passwords must match
              </li>
              <li style={{ color: newPassword !== currentPassword && newPassword.length > 0 ? '#10b981' : 'inherit' }}>
                Must be different from current password
              </li>
            </ul>
          </PasswordRequirements>

          <SubmitButton type="submit" disabled={!isValid() || loading || success}>
            {loading ? 'Changing Password...' : 'Change Password'}
          </SubmitButton>
        </Form>
      </Card>
    </Container>
  );
};

export default ForcePasswordChange;
